/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Card,
  Chip,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  AlternateEmail,
  ArrowBack,
  ArrowForward,
  CheckCircle,
  ContentCopy,
  Dns,
  Forward,
  InfoOutlined,
  OpenInNew,
  PowerSettingsNew,
  Visibility,
  VisibilityOff,
  VpnKey,
  Webhook,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const BREVO_GREEN = "#0B996E";
const BREVO_GREEN_HOVER = "#087C59";

const BREVO_DOMAINS_URL = "https://app.brevo.com/senders/domain/list";
const BREVO_API_KEYS_URL = "https://app.brevo.com/settings/keys/api";
const BREVO_SMTP_URL = "https://app.brevo.com/settings/keys/smtp";
const BREVO_WEBHOOKS_URL = "https://app.brevo.com/webhook/list";

const STEPS = [
  { id: "dns", label: "Inbound domain & DNS", icon: Dns },
  { id: "keys", label: "Brevo keys", icon: VpnKey },
  { id: "webhook", label: "Webhook", icon: Webhook },
  { id: "forwarding", label: "Forwarding", icon: Forward },
  { id: "enable", label: "Enable", icon: PowerSettingsNew },
];

const MX_RECORDS = [
  { host: "inbound domain", type: "MX", priority: "10", value: "inbound1.sendinblue.com" },
  { host: "inbound domain", type: "MX", priority: "20", value: "inbound2.sendinblue.com" },
];

function StepProgress({ steps, activeStep, completedThrough, onStepClick }) {
  return (
    <MDBox
      display="flex"
      flexWrap="wrap"
      gap={1}
      mb={3}
      pb={2}
      borderBottom="1px solid"
      borderColor="divider"
    >
      {steps.map((step, index) => {
        const Icon = step.icon;
        const isActive = index === activeStep;
        const isDone = index < completedThrough;
        const canClick = index <= completedThrough;

        return (
          <MDBox
            key={step.id}
            onClick={canClick ? () => onStepClick(index) : undefined}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 1,
              borderRadius: 2,
              border: "1px solid",
              borderColor: isActive ? BREVO_GREEN : isDone ? "success.light" : "divider",
              bgcolor: isActive ? `${BREVO_GREEN}12` : isDone ? "success.50" : "background.paper",
              cursor: canClick ? "pointer" : "default",
              opacity: canClick ? 1 : 0.55,
              transition: "all 0.15s ease",
              "&:hover": canClick
                ? { borderColor: BREVO_GREEN, boxShadow: "0 1px 4px rgba(0,0,0,0.06)" }
                : {},
            }}
          >
            <Box
              sx={{
                width: 28,
                height: 28,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: isActive ? BREVO_GREEN : isDone ? "success.main" : "grey.200",
                color: isActive || isDone ? "#fff" : "text.secondary",
              }}
            >
              {isDone ? <CheckCircle sx={{ fontSize: 16 }} /> : <Icon sx={{ fontSize: 16 }} />}
            </Box>
            <MDTypography
              variant="button"
              fontWeight={isActive ? "bold" : "regular"}
              color={isActive ? "dark" : "text"}
              sx={{ whiteSpace: "nowrap" }}
            >
              {index + 1}. {step.label}
            </MDTypography>
          </MDBox>
        );
      })}
    </MDBox>
  );
}

function InstructionCard({ title, children, action }) {
  return (
    <MDBox
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: 2,
        border: "1px solid",
        borderColor: "divider",
        bgcolor: "#FAFBFC",
      }}
    >
      <MDTypography variant="button" fontWeight="bold" display="block" mb={1}>
        {title}
      </MDTypography>
      <MDTypography variant="body2" color="text" component="div" sx={{ lineHeight: 1.7 }}>
        {children}
      </MDTypography>
      {action && <MDBox mt={2}>{action}</MDBox>}
    </MDBox>
  );
}

function NumberedSteps({ steps }) {
  return (
    <MDBox
      component="ol"
      sx={{
        m: 0,
        pl: 2.5,
        "& li": { mb: 1.25, lineHeight: 1.65, color: "text.secondary" },
        "& li::marker": { color: BREVO_GREEN, fontWeight: 700 },
      }}
    >
      {steps.map((step, i) => (
        <MDTypography key={i} component="li" variant="body2" color="text">
          {step}
        </MDTypography>
      ))}
    </MDBox>
  );
}

function BrevoLinkButton({ href, children }) {
  return (
    <MDButton
      component="a"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      variant="outlined"
      size="small"
      startIcon={<OpenInNew />}
      sx={{ borderColor: BREVO_GREEN, color: BREVO_GREEN }}
    >
      {children}
    </MDButton>
  );
}

export default function EmailToCaseSettingsPage() {
  const navigate = useNavigate();
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [activeStep, setActiveStep] = useState(0);

  const [showApiKey, setShowApiKey] = useState(false);
  const [showSmtpKey, setShowSmtpKey] = useState(false);

  const [settings, setSettings] = useState(null);
  const [enabled, setEnabled] = useState(false);
  const [supportEmail, setSupportEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [inboundDomain, setInboundDomain] = useState("");
  const [inboundAddress, setInboundAddress] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [smtpLogin, setSmtpLogin] = useState("");
  const [smtpKey, setSmtpKey] = useState("");
  const [reopenOnReply, setReopenOnReply] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/email-to-case/settings");
      const data = res.data || {};
      setSettings(data);
      setEnabled(Boolean(data.enabled));
      setSupportEmail(data.support_email || "");
      setDisplayName(data.support_display_name || "");
      setInboundDomain(data.inbound_domain || "");
      setInboundAddress(data.inbound_address || "");
      setSmtpLogin(data.brevo_smtp_login || "");
      setReopenOnReply(data.reopen_on_reply !== false);
      setApiKey("");
      setSmtpKey("");
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Unable to load Email-to-Case settings."
      );
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const domainReady = Boolean(inboundDomain.trim());
  const keysReady = Boolean(
    (apiKey.trim() || settings?.has_brevo_api_key) &&
      smtpLogin.trim() &&
      (smtpKey.trim() || settings?.has_brevo_smtp_key) &&
      supportEmail.trim()
  );
  const webhookReady = Boolean(settings?.webhook_url);
  const forwardingReady = Boolean(inboundAddress.trim());

  const completedThrough = (() => {
    if (domainReady && keysReady && webhookReady && forwardingReady) return 4;
    if (domainReady && keysReady && webhookReady) return 3;
    if (domainReady && keysReady) return 2;
    if (domainReady) return 1;
    return 0;
  })();

  const copyValue = async (value, label) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setSuccess(`${label} copied.`);
    } catch {
      setError(`Could not copy ${label.toLowerCase()}.`);
    }
  };

  const buildPayload = (extra = {}) => {
    const payload = {
      enabled,
      support_email: supportEmail || null,
      support_display_name: displayName || null,
      inbound_domain: inboundDomain || null,
      inbound_address: inboundAddress || null,
      brevo_smtp_login: smtpLogin || null,
      reopen_on_reply: reopenOnReply,
      ...extra,
    };
    if (apiKey.trim()) payload.brevo_api_key = apiKey.trim();
    if (smtpKey.trim()) payload.brevo_smtp_key = smtpKey.trim();
    return payload;
  };

  const handleSave = async (extra = {}, message = "Email-to-Case settings saved.") => {
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      await axiosInstance.put("/email-to-case/settings", buildPayload(extra));
      setSuccess(message);
      await load();
      return true;
    } catch (err) {
      setError(
        err?.response?.data?.detail || err?.message || "Unable to save Email-to-Case settings."
      );
      return false;
    } finally {
      setSaving(false);
    }
  };

  const goNext = () => {
    if (activeStep === 0 && !domainReady) {
      setError("Enter your Brevo inbound domain before continuing.");
      return;
    }
    if (activeStep === 1 && !keysReady) {
      setError("Enter support email, Brevo API key, SMTP login and SMTP key before continuing.");
      return;
    }
    setError("");
    setActiveStep((s) => Math.min(s + 1, STEPS.length - 1));
  };

  const goBack = () => {
    setError("");
    setActiveStep((s) => Math.max(s - 1, 0));
  };

  const suggestedAddress = inboundDomain.trim() ? `inbox@${inboundDomain.trim()}` : "";
  const publicSupport = supportEmail.trim() || "support@yourcompany.com";
  const isLocalWebhook =
    settings?.webhook_url &&
    /localhost|127\.0\.0\.1|\.local(?:host)?(?:[:/]|$)/i.test(settings.webhook_url);

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Step 1 — Create your Brevo inbound domain
            </MDTypography>

            <InstructionCard
              title="Add a dedicated subdomain for incoming mail"
              action={<BrevoLinkButton href={BREVO_DOMAINS_URL}>Open Brevo domains</BrevoLinkButton>}
            >
              <NumberedSteps
                steps={[
                  "Log in to your own Brevo account (PearlCRM never uses a shared Brevo key for Email-to-Case).",
                  "Pick a subdomain used only for receiving support mail, for example cases.yourcompany.com. It must be different from the domain you send email from.",
                  "In your DNS host (GoDaddy, Cloudflare, Route53, cPanel…), add the two MX records shown below for that subdomain.",
                  "Wait for DNS to propagate (usually a few minutes, up to a few hours).",
                  "Enter the same subdomain in the field below and continue.",
                ]}
              />
            </InstructionCard>

            <MDBox
              sx={{
                mb: 2,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                overflow: "hidden",
                maxWidth: 720,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "#FAFBFC" }}>
                    <TableCell>Host</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Priority</TableCell>
                    <TableCell>Value</TableCell>
                  </TableRow>
                
                <TableBody>
                  {MX_RECORDS.map((rec) => (
                    <TableRow key={rec.priority}>
                      <TableCell>
                        <code>{inboundDomain.trim() || rec.host}</code>
                      </TableCell>
                      <TableCell>{rec.type}</TableCell>
                      <TableCell>{rec.priority}</TableCell>
                      <TableCell>
                        <code>{rec.value}</code>
                        <Tooltip title="Copy value">
                          <IconButton size="small" onClick={() => copyValue(rec.value, "MX value")}>
                            <ContentCopy sx={{ fontSize: 15 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                </TableHead>
              </Table>
            </MDBox>

            <MDBox maxWidth={720}>
              <TextField
                fullWidth
                label="Brevo inbound domain"
                value={inboundDomain}
                onChange={(e) => setInboundDomain(e.target.value)}
                placeholder="cases.yourcompany.com"
                margin="normal"
                autoComplete="off"
                helperText="Subdomain whose MX records point at Brevo. Never your main mail domain."
                sx={{ bgcolor: "#fff" }}
              />
            </MDBox>

            <Alert severity="warning" sx={{ mt: 1, maxWidth: 720 }}>
              Do <strong>not</strong> point MX for your main domain (e.g. <code>yourcompany.com</code>)
              at Brevo — that would break your normal company email. Only the inbound subdomain gets
              Brevo MX records.
            </Alert>
          </>
        );

      case 1:
        return (
          <>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Step 2 — Enter your Brevo keys
            </MDTypography>

            <InstructionCard
              title="Where to find your keys in Brevo"
              action={
                <MDBox display="flex" gap={1} flexWrap="wrap">
                  <BrevoLinkButton href={BREVO_API_KEYS_URL}>API keys</BrevoLinkButton>
                  <BrevoLinkButton href={BREVO_SMTP_URL}>SMTP credentials</BrevoLinkButton>
                </MDBox>
              }
            >
              <NumberedSteps
                steps={[
                  "API key: Brevo → SMTP & API → API keys → Generate a new API key. Copy the xkeysib-… value.",
                  "SMTP login and key: Brevo → SMTP & API → SMTP. The login is usually your Brevo account email; generate an SMTP key if you do not have one.",
                  "SMTP is required because agent replies must carry In-Reply-To and References headers so the customer sees one continuous email thread. Brevo's REST API cannot set those headers.",
                  "Support email is the From address customers see, for example support@yourcompany.com. Verify that sender/domain in Brevo so replies do not land in spam.",
                ]}
              />
            </InstructionCard>

            <MDBox maxWidth={720}>
              <TextField
                fullWidth
                label="Support email (From address)"
                value={supportEmail}
                onChange={(e) => setSupportEmail(e.target.value)}
                placeholder="support@yourcompany.com"
                margin="normal"
                autoComplete="off"
                sx={{ bgcolor: "#fff" }}
              />
              <TextField
                fullWidth
                label="Display name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Acme Support"
                margin="normal"
                autoComplete="off"
                sx={{ bgcolor: "#fff" }}
              />
              <TextField
                fullWidth
                label="Brevo API key"
                type={showApiKey ? "text" : "password"}
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                placeholder={
                  settings?.brevo_api_key_masked
                    ? `Saved: ${settings.brevo_api_key_masked} — enter a new value to replace`
                    : "xkeysib-…"
                }
                margin="normal"
                autoComplete="new-password"
                sx={{ bgcolor: "#fff" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowApiKey((v) => !v)} edge="end" size="small">
                        {showApiKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                fullWidth
                label="Brevo SMTP login"
                value={smtpLogin}
                onChange={(e) => setSmtpLogin(e.target.value)}
                placeholder="you@yourcompany.com"
                margin="normal"
                autoComplete="off"
                sx={{ bgcolor: "#fff" }}
              />
              <TextField
                fullWidth
                label="Brevo SMTP key"
                type={showSmtpKey ? "text" : "password"}
                value={smtpKey}
                onChange={(e) => setSmtpKey(e.target.value)}
                placeholder={
                  settings?.brevo_smtp_key_masked
                    ? `Saved: ${settings.brevo_smtp_key_masked} — enter a new value to replace`
                    : "SMTP key from Brevo"
                }
                margin="normal"
                autoComplete="new-password"
                helperText={`Sent over ${settings?.brevo_smtp_host || "smtp-relay.brevo.com"}:${
                  settings?.brevo_smtp_port || 587
                }`}
                sx={{ bgcolor: "#fff" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowSmtpKey((v) => !v)} edge="end" size="small">
                        {showSmtpKey ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </MDBox>

            <Alert severity="info" icon={<InfoOutlined />} sx={{ mt: 2, maxWidth: 720 }}>
              Keys are stored for your tenant only and shown masked afterwards. Leave a secret field
              blank to keep the saved value.
            </Alert>
          </>
        );

      case 2:
        return (
          <>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Step 3 — Add the inbound webhook in Brevo
            </MDTypography>

            <InstructionCard
              title="How to create the inbound webhook"
              action={<BrevoLinkButton href={BREVO_WEBHOOKS_URL}>Open Brevo webhooks</BrevoLinkButton>}
            >
              <NumberedSteps
                steps={[
                  "Copy the webhook URL below (it already contains your tenant and a secret token).",
                  "In Brevo, create a webhook of type Inbound (inbound parsing), not Transactional or Marketing.",
                  "Set the event to inboundEmailProcessed.",
                  `Set the domain to your inbound domain${
                    inboundDomain.trim() ? ` (${inboundDomain.trim()})` : ""
                  }.`,
                  "Paste the webhook URL as the target URL and save.",
                  "Keep this URL secret — anyone with it can post mail into your CRM. Use Rotate token if it leaks.",
                ]}
              />
            </InstructionCard>

            <MDBox maxWidth={720}>
              <TextField
                fullWidth
                label="Webhook URL — paste this into Brevo"
                value={settings?.webhook_url || ""}
                margin="normal"
                sx={{ bgcolor: "#fff" }}
                InputProps={{
                  readOnly: true,
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy URL">
                        <IconButton
                          onClick={() => copyValue(settings?.webhook_url, "Webhook URL")}
                          edge="end"
                          size="small"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />

              {isLocalWebhook && (
                <Alert severity="info" sx={{ mt: 1.5 }}>
                  <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                    Local URL — Brevo cannot reach this from the internet
                  </MDTypography>
                  <MDTypography variant="body2">
                    Set <strong>API_BASE_URL</strong> in the backend environment to your public HTTPS
                    API (for example <code>https://api.pearlcrm.com</code>), or use a tunnel while
                    testing locally.
                  </MDTypography>
                </Alert>
              )}

              <MDBox mt={2}>
                <MDButton
                  variant="outlined"
                  color="secondary"
                  size="small"
                  disabled={saving}
                  onClick={() =>
                    handleSave({ rotate_webhook_token: true }, "New webhook token generated.")
                  }
                >
                  Rotate webhook token
                </MDButton>
                <MDTypography variant="caption" color="text" display="block" mt={1}>
                  Rotating invalidates the old URL — update it in Brevo immediately afterwards.
                </MDTypography>
              </MDBox>
            </MDBox>
          </>
        );

      case 3:
        return (
          <>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Step 4 — Forward your support address
            </MDTypography>

            <InstructionCard title="How support mail reaches PearlCRM">
              Your public support address stays where it is today. You only add a forwarding rule so a
              copy is delivered to your Brevo inbound address, which then posts to PearlCRM.
              <MDBox
                component="pre"
                sx={{
                  mt: 2,
                  mb: 0,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: "#F3F5F7",
                  fontSize: 12.5,
                  lineHeight: 1.75,
                  overflowX: "auto",
                }}
              >
                {`Customer  →  ${publicSupport}
          →  forward to ${inboundAddress.trim() || suggestedAddress || "inbox@cases.yourcompany.com"}
          →  Brevo inbound parsing (your account)
          →  PearlCRM webhook
          →  New case, or a comment on the existing case`}
              </MDBox>
            </InstructionCard>

            <MDBox maxWidth={720}>
              <TextField
                fullWidth
                label="Inbound address (forward target)"
                value={inboundAddress}
                onChange={(e) => setInboundAddress(e.target.value)}
                placeholder={suggestedAddress || "inbox@cases.yourcompany.com"}
                margin="normal"
                autoComplete="off"
                helperText="Any mailbox name on your inbound domain works — Brevo accepts all of them."
                sx={{ bgcolor: "#fff" }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title="Copy address">
                        <IconButton
                          onClick={() => copyValue(inboundAddress, "Inbound address")}
                          edge="end"
                          size="small"
                        >
                          <ContentCopy />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
              {suggestedAddress && !inboundAddress.trim() && (
                <MDButton
                  variant="outlined"
                  size="small"
                  color="secondary"
                  sx={{ mt: 1 }}
                  onClick={() => setInboundAddress(suggestedAddress)}
                >
                  Use {suggestedAddress}
                </MDButton>
              )}
            </MDBox>

            <MDBox mt={2}>
              <InstructionCard title="Google Workspace (Gmail)">
                <NumberedSteps
                  steps={[
                    `Sign in to the ${publicSupport} mailbox (or Admin console if it is a group).`,
                    "Open Settings → See all settings → Forwarding and POP/IMAP.",
                    "Click Add a forwarding address, enter the inbound address above, and confirm.",
                    "Brevo will not click a confirmation link, so use Admin console → Apps → Google Workspace → Gmail → Routing to add a recipient-based route instead if Gmail asks for verification.",
                    "For a Google Group, simply add the inbound address as a member with delivery enabled.",
                    "Choose Keep a copy in the inbox so your team still sees the original mail.",
                  ]}
                />
              </InstructionCard>

              <InstructionCard title="Microsoft 365 / Outlook">
                <NumberedSteps
                  steps={[
                    "Open Microsoft 365 admin center → Users → Active users (or Teams & groups for a shared mailbox).",
                    `Select ${publicSupport} → Mail tab → Manage email forwarding.`,
                    "Enable Forward all emails sent to this mailbox and enter the inbound address above.",
                    "Tick Keep a copy of forwarded email so the mailbox still retains messages.",
                    "Save.",
                  ]}
                />
              </InstructionCard>

              <InstructionCard title="cPanel / other hosting">
                <NumberedSteps
                  steps={[
                    "Open Email → Forwarders → Add Forwarder.",
                    `Set the address to forward as ${publicSupport}.`,
                    "Set the destination to the inbound address above and save.",
                  ]}
                />
              </InstructionCard>
            </MDBox>
          </>
        );

      case 4:
        return (
          <>
            <MDTypography variant="h6" fontWeight="medium" mb={2}>
              Step 5 — Enable & save
            </MDTypography>

            <MDBox
              sx={{
                p: 2.5,
                borderRadius: 2,
                border: "1px solid",
                borderColor: "divider",
                bgcolor: "#fff",
                maxWidth: 720,
              }}
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(e) => setEnabled(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: BREVO_GREEN },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        bgcolor: BREVO_GREEN,
                      },
                    }}
                  />
                }
                label={
                  <MDBox>
                    <MDTypography variant="body1" fontWeight="medium">
                      Enable Email-to-Case
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      Incoming support mail creates cases; replies from the case screen are emailed
                      back on the same thread.
                    </MDTypography>
                  </MDBox>
                }
                sx={{ alignItems: "flex-start", ml: 0 }}
              />

              <Divider sx={{ my: 2 }} />

              <FormControlLabel
                control={
                  <Switch
                    checked={reopenOnReply}
                    onChange={(e) => setReopenOnReply(e.target.checked)}
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": { color: BREVO_GREEN },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                        bgcolor: BREVO_GREEN,
                      },
                    }}
                  />
                }
                label={
                  <MDBox>
                    <MDTypography variant="body1" fontWeight="medium">
                      Reopen closed cases on customer reply
                    </MDTypography>
                    <MDTypography variant="caption" color="text" display="block">
                      A reply to a closed case moves it back to {settings?.default_status || "Open"}.
                    </MDTypography>
                  </MDBox>
                }
                sx={{ alignItems: "flex-start", ml: 0 }}
              />
            </MDBox>

            {!keysReady && (
              <Alert severity="error" sx={{ mt: 2, maxWidth: 720 }}>
                Complete step 2 — support email, Brevo API key, SMTP login and SMTP key are required
                before enabling.
              </Alert>
            )}

            <InstructionCard title="Test it end to end">
              <NumberedSteps
                steps={[
                  `Send an email from an outside address to ${publicSupport}.`,
                  "Open Cases in PearlCRM — a new case appears with the email body as the description and a customer comment.",
                  "Add a staff comment on that case. It is emailed to the customer as a reply on the same thread.",
                  "Reply from the customer mailbox — it becomes a new comment on the same case instead of a second case.",
                ]}
              />
            </InstructionCard>

            <InstructionCard title="If mail does not arrive">
              <NumberedSteps
                steps={[
                  "Check the MX records on the inbound domain resolve to inbound1/inbound2.sendinblue.com.",
                  "Check the forwarding rule is active and mail is actually leaving your mailbox.",
                  "Check the Brevo webhook is type Inbound, event inboundEmailProcessed, and points to the exact URL from step 3.",
                  "Check the webhook URL host is publicly reachable over HTTPS (API_BASE_URL in the backend).",
                  "Confirm Email-to-Case is enabled here and the keys are saved.",
                ]}
              />
            </InstructionCard>
          </>
        );

      default:
        return null;
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 3 : 4} pb={6} px={isV2 ? 0 : 2}>
        {isV2 ? (
          <PageHeader
            title="Email to Case"
            summary="Connect your own Brevo account so support email becomes cases, and agent replies stay on the same email thread."
            actions={(
              <>
                {settings?.configured && settings?.enabled && (
                  <Chip icon={<CheckCircle />} label="Live" color="success" size="small" />
                )}
                {settings?.configured && !settings?.enabled && (
                  <Chip label="Configured · Disabled" color="warning" size="small" />
                )}
                {!settings?.configured && !loading && <Chip label="Not connected" size="small" />}
              </>
            )}
          />
        ) : (
        <MDBox display="flex" alignItems="flex-start" gap={2} mb={3} flexWrap="wrap">
          <IconButton onClick={() => navigate("/settings")} sx={{ mt: 0.5 }}>
            <ArrowBack />
          </IconButton>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: `${BREVO_GREEN}15`,
              color: BREVO_GREEN,
              mt: 0.25,
            }}
          >
            <AlternateEmail />
          </Box>
          <MDBox flex={1} minWidth={240}>
            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap" mb={0.5}>
              <MDTypography variant="h4" fontWeight="medium" color="dark">
                Email to Case
              </MDTypography>
              {settings?.configured && settings?.enabled && (
                <Chip icon={<CheckCircle />} label="Live" color="success" size="small" />
              )}
              {settings?.configured && !settings?.enabled && (
                <Chip label="Configured · Disabled" color="warning" size="small" />
              )}
              {!settings?.configured && !loading && <Chip label="Not connected" size="small" />}
            </MDBox>
            <MDTypography variant="body2" color="text">
              Connect your own Brevo account so support email becomes cases, and agent replies stay on
              the same email thread.
            </MDTypography>
          </MDBox>
        </MDBox>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {typeof error === "string" ? error : JSON.stringify(error)}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
            {success}
          </Alert>
        )}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress sx={{ color: BREVO_GREEN }} />
          </MDBox>
        ) : (
          <Card
            sx={{
              p: { xs: 2, md: 3 },
              boxShadow: "0 2px 12px rgba(0,0,0,0.06)",
              borderRadius: 2,
              bgcolor: "#fff",
            }}
          >
            <StepProgress
              steps={STEPS}
              activeStep={activeStep}
              completedThrough={completedThrough}
              onStepClick={setActiveStep}
            />

            <MDBox minHeight={300}>{renderStepContent()}</MDBox>

            <Divider sx={{ my: 3 }} />

            <MDBox
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexWrap="wrap"
              gap={1}
            >
              <MDButton
                color="secondary"
                onClick={activeStep === 0 ? () => navigate("/settings") : goBack}
                startIcon={activeStep > 0 ? <ArrowBack /> : null}
              >
                {activeStep === 0 ? "Back to settings" : "Previous step"}
              </MDButton>

              <MDBox display="flex" gap={1} flexWrap="wrap">
                <MDButton
                  variant="outlined"
                  color="secondary"
                  onClick={() => handleSave()}
                  disabled={saving}
                >
                  Save progress
                </MDButton>

                {activeStep < STEPS.length - 1 ? (
                  <MDButton
                    variant="contained"
                    disableElevation
                    endIcon={<ArrowForward />}
                    onClick={goNext}
                    sx={{
                      backgroundColor: BREVO_GREEN,
                      color: "#fff !important",
                      "&:hover": {
                        backgroundColor: BREVO_GREEN_HOVER,
                        color: "#fff !important",
                      },
                    }}
                  >
                    Next step
                  </MDButton>
                ) : (
                  <MDButton
                    variant="contained"
                    disableElevation
                    onClick={() => handleSave()}
                    disabled={saving || (enabled && !(keysReady && domainReady))}
                    sx={{
                      backgroundColor: BREVO_GREEN,
                      color: "#fff !important",
                      minWidth: 170,
                      "&:hover": {
                        backgroundColor: BREVO_GREEN_HOVER,
                        color: "#fff !important",
                      },
                    }}
                  >
                    {saving ? <CircularProgress size={20} color="inherit" /> : "Save connection"}
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
          </Card>
        )}
      </MDBox>
    </DashboardLayout>
  );
}
